import * as React from 'react'
import { createPortal } from 'react-dom'
import { X, HelpCircle } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useHelp } from '@/shared/features/help/HelpContext'
import { useEscapeLayer } from './escapeLayer'
import {
  focusableWithin,
  focusReturnTarget,
  initialFocusIndex,
  nextOpener,
  trapTarget,
} from './focusTrap'
import { DialogA11yContext, type DialogA11y } from './dialogA11y'

// Layout effect in the browser (registers before paint, so the panel is named
// on its first frame), plain effect under a static server render.
const useIsoLayoutEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect

const Dialog = ({
  open,
  onOpenChange,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}) => {
  const overlayRef = React.useRef<HTMLDivElement>(null)
  const titleId = React.useId()
  const descriptionId = React.useId()
  const [hasTitle, setHasTitle] = React.useState(false)
  const [hasDescription, setHasDescription] = React.useState(false)
  const a11y = React.useMemo<DialogA11y>(
    () => ({ titleId, descriptionId, hasTitle, hasDescription, setHasTitle, setHasDescription }),
    [titleId, descriptionId, hasTitle, hasDescription],
  )

  // Escape closes the top-most layer only: a dropdown or date picker open
  // inside this dialog registers above it and takes the key first.
  useEscapeLayer(open, () => onOpenChange(false))

  // Record the opener during render on the open transition, before children
  // commit. An autoFocus field inside the dialog takes focus in the commit's
  // layout phase, earlier than any effect, so the effect below cannot read it.
  const openerRef = React.useRef<HTMLElement | null>(null)
  const wasOpenRef = React.useRef(false)
  if (open !== wasOpenRef.current) {
    const active =
      typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null)
    openerRef.current = nextOpener(wasOpenRef.current, open, openerRef.current, active)
    wasOpenRef.current = open
  }

  // Move focus in on open, hand it back to the opener on close. A field with
  // autoFocus has already taken focus by the time this runs, so it is kept.
  React.useEffect(() => {
    if (!open) return
    const opener = openerRef.current
    const overlay = overlayRef.current
    if (overlay && !overlay.contains(document.activeElement)) {
      const panel = overlay.querySelector<HTMLElement>('[role="dialog"]') ?? overlay
      const focusables = focusableWithin(panel)
      const index = initialFocusIndex(focusables)
      if (index >= 0) focusables[index].focus()
      else panel.focus()
    }
    return () => {
      focusReturnTarget(opener)?.focus()
    }
  }, [open])

  // Tab and Shift+Tab wrap inside the dialog so focus never reaches the page
  // behind it. Popovers portaled out of the panel (dropdown lists) are left to
  // the browser: focus is not inside the panel's DOM while they are open.
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab' || e.defaultPrevented) return
    const overlay = overlayRef.current
    if (!overlay) return
    const active = document.activeElement as HTMLElement | null
    if (active && !overlay.contains(active)) return
    const panel = overlay.querySelector<HTMLElement>('[role="dialog"]') ?? overlay
    const focusables = focusableWithin(panel)
    const current = active ? focusables.indexOf(active) : -1
    const target = trapTarget(focusables.length, current, e.shiftKey)
    if (focusables.length === 0) {
      e.preventDefault()
      return
    }
    if (target === null) return
    e.preventDefault()
    focusables[target].focus()
  }

  if (!open) return null

  return createPortal(
    // Scroll-on-overlay pattern: the backdrop is fixed, and the alignment
    // wrapper uses min-h-full inside an overflow-y-auto container. Short
    // dialogs stay centered; a dialog taller than the viewport (long forms on
    // a phone) makes the overlay scroll instead of clipping off-screen.
    <DialogA11yContext.Provider value={a11y}>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-50 overflow-y-auto text-left"
        onKeyDown={onKeyDown}
      >
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden />
        {/* Phone: the panel is pinned to the bottom edge as a sheet, which puts
            it under the thumb and leaves the context above it visible. Tablet up
            (sm+): the classic centered modal, unchanged.
            The alignment wrapper covers the backdrop, so click-to-close lives
            here: only a click on the padding itself (target === wrapper), not on
            the panel, dismisses. On a phone the sheet leaves no padding to click,
            so the header's close control is the escape route. */}
        <div
          className="relative flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) onOpenChange(false)
          }}
        >
          {children}
        </div>
      </div>
    </DialogA11yContext.Provider>,
    document.body,
  )
}

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { onClose?: () => void }
>(({ className, children, onClose, ...props }, ref) => {
  const a11y = React.useContext(DialogA11yContext)
  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={a11y?.hasTitle ? a11y.titleId : undefined}
      aria-describedby={a11y?.hasDescription ? a11y.descriptionId : undefined}
      tabIndex={-1}
      className={cn(
        'focus:outline-none',
        // Q4-BRES8 / Q5-BPHONE6: dialogs render in a portal outside <main>,
        // so they carry the 44px touch rules themselves.
        'touch-targets',
        'relative z-10 w-full max-w-lg bg-white shadow-2xl',
        // Phone: bottom sheet. Square the bottom corners so it sits flush on the
        // screen edge, cap the height so a long form scrolls inside the sheet
        // rather than pushing its own header off-screen, and clear the home
        // indicator. Tablet up: the previous centered card, corner for corner.
        'rounded-t-2xl sm:rounded-2xl',
        // Tablet up: capped to the viewport and scrolling inside, so a tall form
        // (New inventory item at 1280x800) keeps its sticky footer on screen
        // instead of pushing Save/Cancel below the fold.
        'max-h-[92dvh] overflow-y-auto sm:max-h-[calc(100dvh-2rem)]',
        'pb-[env(safe-area-inset-bottom)] sm:pb-0',
        'animate-sheet-up sm:animate-slide-up motion-reduce:animate-none',
        className,
      )}
      {...props}
    >
      {/* Zero-height sticky rail so the dismiss control stays reachable while a
        tall sheet scrolls under it. Rendered before children (sticky tracks
        scroll position, not DOM order, but it has to start at the top to pin
        there). The hit box is 44px on touch, the a11y floor, while the glyph
        stays the same size. */}
      {onClose && (
        <div className="sticky top-0 z-30 flex h-0 justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-dialog-chrome=""
            className="mr-1.5 mt-1.5 flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 sm:mr-4 sm:mt-4 sm:h-8 sm:w-8"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {children}
    </div>
  )
})
DialogContent.displayName = 'DialogContent'

// Left-aligned at every width: one header reads `Icon Title (help) X` on a
// phone and a desktop alike. Centring below sm put a plain title in the
// middle while a title with a Help button (a flex row) stayed on the left.
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-left px-5 py-5 sm:px-6 border-b border-gray-100',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

// UX-21: one footer layout at every width. On a phone the sheet scrolls inside
// itself, so the footer is pinned to its bottom edge (sticky, opaque) and the
// actions stay side by side in the desktop order (Cancel, then the primary
// action) instead of stacking with Cancel pushed below the fold. Helper text
// (a <p> child, e.g. why Save is disabled) moves above the buttons on a phone.
const DIALOG_FOOTER_CLASS = cn(
  'flex flex-row flex-wrap items-center justify-end gap-2 px-5 py-4 sm:px-6 sm:py-6 border-t border-gray-100',
  // Q5-BPHONE1: pinned at every width. The panel scrolls inside itself at sm+
  // too, so a static footer at 844x390 (landscape phone) scrolled Save off
  // screen while portrait kept it pinned.
  'sticky bottom-0 z-20 bg-white',
  '[&>p]:order-first [&>p]:w-full sm:[&>p]:order-none sm:[&>p]:w-auto',
)

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(DIALOG_FOOTER_CLASS, className)} {...props} />
)
DialogFooter.displayName = 'DialogFooter'

/** Standard dialog body padding (px-6 py-5), so dialogs stop hand-rolling it. */
const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 py-5 sm:px-6', className)} {...props} />
)
DialogBody.displayName = 'DialogBody'

const DialogTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & { docId?: string }
>(({ className, docId, children, id, ...props }, ref) => {
  const { openArticle } = useHelp()
  const a11y = React.useContext(DialogA11yContext)
  const setHasTitle = a11y?.setHasTitle
  useIsoLayoutEffect(() => {
    if (!setHasTitle) return
    setHasTitle(true)
    return () => setHasTitle(false)
  }, [setHasTitle])
  const titleId = id ?? a11y?.titleId
  return (
    <h2
      ref={ref}
      // Q3-FIN7: with a Help button inside, the dialog is named by the title
      // text alone (the span below), never "Title Help".
      id={docId ? undefined : titleId}
      className={cn(
        'text-lg font-semibold leading-none tracking-tight text-brand-rail',
        // Reserve right-edge space for the absolute-positioned close X
        // (DialogContent renders it at `right-4 top-4` ~= 16px in). Without
        // pr-8 the title text can run under the X on narrow dialogs.
        docId && 'flex items-center gap-2 pr-8',
        !docId && 'pr-8',
        className,
      )}
      {...props}
    >
      {/* min-w-0: the title text is a flex item next to the Help button, so
          without it a child that truncates (a long room number) cannot shrink
          and widens the dialog instead. The span is a flex row of its own so a
          leading icon sits beside the text (Icon Title (help) X); as a plain
          inline span the block-level svg pushed the text onto a second line. */}
      {docId ? (
        <span id={titleId} className="flex min-w-0 items-center gap-2 [&>svg]:shrink-0">
          {children}
        </span>
      ) : (
        children
      )}
      {docId && (
        <button
          aria-label="Help"
          title="Help"
          type="button"
          data-dialog-chrome=""
          onClick={() => openArticle(docId)}
          // Sits inline next to the title text (not pushed to the right
          // edge) so it never collides with the close X in the dialog's
          // top-right corner.
          // Q4-BFIN8: a 44px tap area around the 14px icon; the negative
          // margin keeps the title row the same height.
          className="-m-[15px] inline-flex h-11 w-11 shrink-0 items-center justify-center text-gray-400 hover:text-brand-orange transition-colors"
        >
          <HelpCircle size={14} />
        </button>
      )}
    </h2>
  )
})
DialogTitle.displayName = 'DialogTitle'

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, id, ...props }, ref) => {
  const a11y = React.useContext(DialogA11yContext)
  const setHasDescription = a11y?.setHasDescription
  useIsoLayoutEffect(() => {
    if (!setHasDescription) return
    setHasDescription(true)
    return () => setHasDescription(false)
  }, [setHasDescription])
  return (
    <p
      ref={ref}
      id={id ?? a11y?.descriptionId}
      className={cn('text-sm text-gray-600 mt-1', className)}
      {...props}
    />
  )
})
DialogDescription.displayName = 'DialogDescription'

// Maintain legacy support for simpler usage
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  loading = false,
  danger = false,
  children,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description?: string
  confirmLabel?: string
  loading?: boolean
  danger?: boolean
  /** Optional extra fields (e.g. a reason box) shown between text and buttons. */
  children?: React.ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent onClose={onClose} className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children && <DialogBody className="pt-0">{children}</DialogBody>}
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-50',
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-orange hover:bg-brand-orange-dark',
            )}
          >
            {loading ? 'Loading…' : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
